import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Modal,
  Paper,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
  Tooltip,
  FormLabel,
  CircularProgress,
} from "@mui/material";

import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import useCommonContext from "../../store/CommonContext";
import ApiEndpoints from "../../network/ApiEndPoints";
import { postJsonData } from "../../network/ApiController";
import ModalHeader from "../../modals/ModalHeader";
import ModalFooter from "../../modals/ModalFooter";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import AuthContext from "../../store/AuthContext";
import { useGeolocated } from "react-geolocated";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LocationFetcher from "./Common/Location";
import { Cancel } from "@mui/icons-material";
import SmallLoader from "../../component/loading-screen/SmallLoader";
import Loader from "../../component/loading-screen/Loader";
import Spinner from "../../component/loading-screen/Spinner";
import CustomSpinner from "../../component/loading-screen/CustomSpinner";
import { fileToBase64, splitIntoFlatChunks } from "../../utils/ImageUtil";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: {
    xs: "95%", // 95% width on extra small screens
    sm: "90%", // 90% width on small screens
    md: 700, // Fixed 700px on medium and larger screens
  },
  maxWidth: 700, // Still respect the maximum width
  maxHeight: "90vh", // Prevent modal from being too tall
  overflow: "auto", // Allow scrolling if content is too long
  bgcolor: "background.paper",
  boxShadow: 24,
  p: {
    xs: 2, // Smaller padding on mobile
    sm: 4, // Standard padding on larger screens
  },
  borderRadius: 2,
};

const MarkAttendanceModal = ({
  mode = "in",
  title,
  refresh,
  row,
  disabled,
  triggerButton = null,
  autoOpen = false,
}) => {
  const { refreshUser } = useCommonContext();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("Office");
  const [request, setRequest] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("user");
  const [userCoords, setUserCoords] = useState({
    lat: null,
    long: null,
    address: null,
  });
  const videoRef = useRef(null);

  const authCtx = useContext(AuthContext);
  const user = authCtx.user;
  const loc = authCtx.location;
  const OFFICE_COORDINATES = { lat: 28.72, long: 77.06 };

  const toRad = (deg) => (deg * Math.PI) / 180;

  const distanceMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };

  const round2 = (num) => (num != null ? Math.round(num * 100) / 100 : null);

  const isAtOffice = () => {
    const { lat, long } = userCoords;
    if (!lat || !long) return false;

    const roundedUserLat = round2(lat);
    const roundedUserLong = round2(long);
    const roundedOfficeLat = round2(OFFICE_COORDINATES.lat);
    const roundedOfficeLong = round2(OFFICE_COORDINATES.long);

    // ✅ If both match (after rounding), count as "at office"
    return (
      roundedUserLat === roundedOfficeLat &&
      roundedUserLong === roundedOfficeLong
    );
  };

  const config = {
    in: {
      apiEndpoint: ApiEndpoints.DO_ATTENDANCE,
      photoField: "in_selfie",
      buttonText: "Mark In",
      welcomeText: `Welcome Back, ${
        user?.name || ""
      }! You are marking IN attendance`,
      successMessage: "Checked in successfully",
    },
    out: {
      apiEndpoint: `${ApiEndpoints.OUT_ATTENDANCE}/${row?.id}`,
      photoField: "out_selfie",
      buttonText: "Mark Out",
      welcomeText: `Are you sure you want to punch out, ${
        user?.name || ""
      }? You are marking OUT attendance`,
      successMessage: "Checked out successfully",
    },
  };

  const currentConfig = config[mode];

  const handleOpen = () => {
    setOpen(true);
    startCamera();
  };

  const handleClose = () => {
    setOpen(false);
    stopCamera();
    setPhoto(null);
  };

  const handleSubmit = async () => {
    try {
      const { lat, long } = userCoords;

      if (!lat || !long) {
        apiErrorToast({
          message: "Location not available. Please enable location services.",
        });
        return;
      }

      if (!photo) {
        apiErrorToast({ message: "Photo is required" });
        return;
      }

      const payload = {
        id: user?.id,
        type,
       lat: String(lat),  // Convert lat to string
      long: String(long), // Convert long to string
      };

      /* --------------------------------
         CONVERT SELFIE TO CHUNKS
      ----------------------------------*/
      const base64Image = await fileToBase64(photo);

      payload[currentConfig.photoField] = splitIntoFlatChunks(
        base64Image,
        currentConfig.photoField,
      );

      postJsonData(
        currentConfig.apiEndpoint,
        payload,
        setRequest,
        (res) => {
          if (refresh) refresh();
          refreshUser();
          okSuccessToast(res.data.message || currentConfig.successMessage);
          handleClose();
        },
        (err) => {
          if (refresh) refresh();
          apiErrorToast(err);
        },
      );
    } catch (err) {
      console.error(err);
      apiErrorToast("Attendance submission failed");
    }
  };

  const startCamera = () => {
    if (navigator.mediaDevices?.getUserMedia) {
      // First try to get front camera
      const constraints = {
        video: {
          facingMode: "user", // Remove 'exact' to allow fallback
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((videoStream) => {
          setStream(videoStream);
          if (videoRef.current) {
            videoRef.current.srcObject = videoStream;
          }
        })
        .catch((err) => {
          console.error("Error accessing front camera:", err);

          // If front camera fails, try any available camera
          navigator.mediaDevices
            .getUserMedia({
              video: {
                width: { ideal: 1280 },
                height: { ideal: 720 },
              },
            })
            .then((videoStream) => {
              setStream(videoStream);
              if (videoRef.current) {
                videoRef.current.srcObject = videoStream;
              }
            })
            .catch((fallbackErr) => {
              console.error("Fallback camera error:", fallbackErr);
              apiErrorToast({
                message:
                  "Camera access denied. Please allow camera permissions.",
              });
            });
        });
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      apiErrorToast({ message: "Camera not ready. Please try again." });
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const imageFile = new File([blob], "attendance.jpg", {
        type: "image/jpeg",
      });
      setPhoto(imageFile);
      stopCamera();
    }, "image/jpeg");
  };

  const isSubmitDisabled = () => {
    if (!photo) return true;
    if (type === "Office" && !isAtOffice()) return true;
    if (!userCoords.lat || !userCoords.long) return true; // block if no location
    return false;
  };

  useEffect(() => {
    if (autoOpen) {
      setOpen(true);
      startCamera();
    }
  }, [autoOpen]);

  return (
    <>
      {!autoOpen &&
        (triggerButton ? (
          React.cloneElement(triggerButton, {
            onClick: handleOpen,
            disabled: disabled,
          })
        ) : (
          <Button
            disabled={disabled}
            onClick={handleOpen}
            variant="contained"
            color="primary"
            size="small"
          >
            {currentConfig.buttonText}
          </Button>
        ))}

      <Modal
        open={open}
        onClose={handleClose}
        sx={{
          display: "flex",
          alignItems: { xs: "flex-end", sm: "center" }, // bottom on mobile, center on desktop
          justifyContent: "center",
          p: 1, // some margin for mobile
        }}
      >
        <Box sx={style}>
          <ModalHeader
            title={title || "Attendance"}
            handleClose={handleClose}
          />
          <Spinner loading={request} />
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              fontSize: { xs: "1.1rem", sm: "1.25rem" }, // Responsive font size
              fontWeight: 500,
              lineHeight: 1.3,
              mb: 2,
            }}
          >
            {currentConfig.welcomeText}
          </Typography>

          {/* LocationFetcher */}
          <LocationFetcher
            onLocationFetched={(loc) =>
              setUserCoords({
                lat: loc.latitude,
                long: loc.longitude,
                address: loc.address,
              })
            }
          />

          {(!userCoords.lat || !userCoords.long) && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block", textAlign: "center", mb: 2 }}
            >
              Unable to fetch your location. Please enable GPS/location
              services, allow browser permissions.
            </Typography>
          )}

          <FormControl component="fieldset" sx={{ width: "100%", mb: 2 }}>
            <RadioGroup
              row
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={{
                justifyContent: "center",
                gap: { xs: 2, sm: 4 },
                flexWrap: { xs: "wrap", sm: "nowrap" }, // Wrap on mobile if needed
              }}
            >
              <FormControlLabel
                value="Office"
                control={<Radio size="small" />}
                label="Office"
                sx={{ margin: 0 }}
              />
              <FormControlLabel
                value="WFH"
                control={<Radio size="small" />}
                label="WFH"
                sx={{ margin: 0 }}
              />
            </RadioGroup>
          </FormControl>

          {type === "Office" && (
            <Box
              sx={{
                textAlign: "center",
                p: 1.5,
                borderRadius: 2,
                backgroundColor: isAtOffice() ? "success.light" : "error.light",
                color: "white",
                fontSize: { xs: "0.85rem", sm: "0.9rem" },
                mb: 2,
              }}
            >
              {isAtOffice() ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <CheckCircleIcon fontSize="small" /> You are in office
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Cancel fontSize="small" /> You are not in office
                </Box>
              )}

              <Box
                sx={{
                  mt: 1,
                  fontSize: "0.75rem",
                  opacity: 0.9,
                  wordBreak: "break-word", // Prevent long coordinate overflow
                }}
              >
                <div>
                  Your location: {round2(userCoords.lat)},{" "}
                  {round2(userCoords.long)}
                </div>
                <div>
                  Office: {round2(OFFICE_COORDINATES.lat)},{" "}
                  {round2(OFFICE_COORDINATES.long)}
                </div>
              </Box>
            </Box>
          )}

          {/* Camera/Photo Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            {photo ? (
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "300px",
                }}
              >
                <img
                  src={URL.createObjectURL(photo)}
                  alt="Captured"
                  style={{
                    width: "100%",
                    borderRadius: "12px",
                    border: "2px solid #2F3B57",
                    aspectRatio: "4/3",
                    objectFit: "cover",
                  }}
                />
                <IconButton
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "white",
                    color: "red",
                    boxShadow: 2,
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                  onClick={() => {
                    setPhoto(null);
                    startCamera();
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "300px",
                  borderRadius: "12px",
                  overflow: "hidden",
                  aspectRatio: "4/3",
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            )}

            {!photo && (
              <Button
                variant="contained"
                onClick={capturePhoto}
                startIcon={<CameraAltIcon />}
                sx={{
                  borderRadius: "50px",
                  textTransform: "none",
                  px: 3,
                  py: 1,
                  backgroundColor: "#2F3B57",
                  "&:hover": { backgroundColor: "#1f2a40" },
                }}
              >
                Capture Photo
              </Button>
            )}
          </Box>

          <ModalFooter
            btn="Submit"
            disable={isSubmitDisabled()}
            onClick={handleSubmit}
          />
        </Box>
      </Modal>
    </>
  );
};
export default MarkAttendanceModal;
